import React, { useState, useEffect } from "react";
import { useBusinessTheme } from "src/shared/hooks/useBusinessTheme";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import { Category, ProductFormData, StaffMember } from "src/shared/services/interfacev2";
import ProductForm from "./ProductForm";

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: ProductFormData) => Promise<void> | void;
  categories: Category[];
  staffMembers: StaffMember[];
  businessType: string;
  loading?: boolean;
  initialData?: Partial<ProductFormData>;
  addProductError?: string;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ open, onClose, onSubmit, categories, staffMembers, businessType, loading = false, initialData, addProductError = "" }) => {
  useBusinessTheme();

  const initialFormState: ProductFormData = {
    productName: initialData?.productName || "",
    arabicName: initialData?.arabicName || "",
    amount: initialData?.amount || "",
    categoryId: initialData?.categoryId || "",
    type: initialData?.type || "",
    staffList: initialData?.staffList || [],
    image: (initialData?.image as File | null) ?? null,
    ownProduct: initialData?.ownProduct || false,
    displayOrder: initialData?.displayOrder || "",
    barcodeId: initialData?.barcodeId || "",
  };

  const [formData, setFormData] = useState<ProductFormData>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    productName?: string;
    arabicName?: string;
  }>({});

  // Barcode state - matching the working example
  const [barcodeMode, setBarcodeMode] = useState(false);
  const [scanMode, setScanMode] = useState(false);

  const getCategoryInitials = (categoryName: string): string => {
    const words = categoryName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 5).toUpperCase();
    } else {
      return words.map((word) => word[0].toUpperCase()).join("");
    }
  };

  useEffect(() => {
    if (open) {
      setFormData(initialFormState);
      setErrors({});
      // Reset barcode state when modal opens
      setBarcodeMode(!!initialFormState.barcodeId);
      setScanMode(false);
    }
  }, [open]);

  const setField = (field: keyof ProductFormData, value: any) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value };

      if (field === "categoryId" && value) {
        const selectedCategory = categories.find((cat) => cat._id.$oid === value);
        if (selectedCategory && newFormData.productName) {
          const categoryInitials = getCategoryInitials(selectedCategory.name);
          const nameWithoutOldInitials = newFormData.productName.replace(/\[\w+\]\s*$/, "").trim();
          newFormData.productName = `${nameWithoutOldInitials} [${categoryInitials}]`;
        }
      }

      return newFormData;
    });

    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isFormValid = () => {
    const amountValue = parseFloat(formData.amount);
    return formData.productName.trim() !== "" && formData.arabicName.trim() !== "" && !isNaN(amountValue) && amountValue > 0 && formData.categoryId.trim() !== "";
  };

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Barcode handlers - matching the working example
  const handleToggleScan = () => {
    setScanMode((prev) => !prev);
  };

  const handleBarcodeDetected = (code: string) => {
    setField("barcodeId", code);
    setBarcodeMode(true); // Show the barcode
    setScanMode(false); // Close scanner
  };

  if (!open) return null;

  return (
    <ModalWrapper title="Add Product" onClose={onClose} width="max-w-lg">
      <ProductForm
        formData={formData}
        categories={categories}
        staffMembers={staffMembers}
        businessType={businessType}
        onChange={setField}
        showErrors={isSubmitting}
        barcodeId={formData.barcodeId}
        barcodeMode={barcodeMode}
        scanMode={scanMode}
        onToggleScan={handleToggleScan}
        onBarcodeDetected={handleBarcodeDetected}
      />

      <div className="flex justify-end gap-3 mt-6">
        <CommonButtons className="w-32 h-10" label="Cancel" variant="outlineDanger" onClick={onClose} />
        <CommonButtons
          className="w-32 h-10"
          label={isSubmitting || loading ? <LoadingScreen /> : "Confirm"}
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting || loading}
        />
      </div>
    </ModalWrapper>
  );
};

export default AddProductModal;
